import { useState, useEffect } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Select, SelectItem } from '@heroui/react';
import { useUpdatePageScale } from '../../api/knowledge';

const COMMON_SCALES = [
  { label: '1/4" = 1\'-0"', ratio: 48 },    // 1 foot = 48 * (1/4") on paper
  { label: '1/8" = 1\'-0"', ratio: 96 },
  { label: '3/16" = 1\'-0"', ratio: 64 },
  { label: '3/8" = 1\'-0"', ratio: 32 },
  { label: '1/2" = 1\'-0"', ratio: 24 },
  { label: '1" = 1\'-0"', ratio: 12 },
  { label: '1:50 (metric)', ratio: 50 },
  { label: '1:100 (metric)', ratio: 100 },
];

const UNITS = [
  { key: 'ft', label: 'Feet' },
  { key: 'm', label: 'Meters' },
  { key: 'in', label: 'Inches' },
  { key: 'cm', label: 'Centimeters' },
];

type ScaleMode = 'line' | 'known';

interface ScaleCalibrationProps {
  pageId: string;
  currentScale: number | null;
  currentUnit: string;
}

export default function ScaleCalibration({ pageId, currentScale, currentUnit }: ScaleCalibrationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<ScaleMode>('line');
  const [pixelLength, setPixelLength] = useState<number | null>(null);
  const [realValue, setRealValue] = useState('');
  const [unit, setUnit] = useState(currentUnit || 'ft');
  const updateScale = useUpdatePageScale();

  // Listen for line-based calibration events
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setPixelLength(detail.pixelLength);
      setMode('line');
      setIsOpen(true);
    };
    window.addEventListener('calibrate-line', handler);
    return () => window.removeEventListener('calibrate-line', handler);
  }, []);

  // Listen for known-scale direct open
  useEffect(() => {
    const handler = () => {
      setPixelLength(null);
      setMode('known');
      setIsOpen(true);
    };
    window.addEventListener('open-known-scale', handler);
    return () => window.removeEventListener('open-known-scale', handler);
  }, []);

  const handleCalibrate = () => {
    if (!pixelLength || !realValue) return;
    const ppu = pixelLength / parseFloat(realValue);
    updateScale.mutate(
      { pageId, pixelsPerUnit: ppu, unit },
      { onSuccess: () => { setIsOpen(false); setRealValue(''); } },
    );
  };

  const handleManualScale = (ratio: number, scaleUnit: string) => {
    // At 300 DPI, ratio tells us how many real-world units per drawing unit
    // For imperial: ratio is the scale factor (e.g., 48 for 1/4"=1'-0")
    // pixels_per_foot = DPI / (scale_inches_per_foot) = 300 * 12 / ratio
    // For metric: pixels_per_meter = DPI * 39.37 / ratio (39.37 in/m)
    const DPI = 300;
    let ppu: number;
    if (scaleUnit === 'ft') {
      ppu = (DPI * 12) / ratio; // e.g., 1/4"=1'-0": 300*12/48 = 75 px/ft
    } else {
      ppu = (DPI * 39.37) / ratio; // metric: px/meter
    }
    updateScale.mutate(
      { pageId, pixelsPerUnit: ppu, unit: scaleUnit },
      { onSuccess: () => setIsOpen(false) },
    );
  };

  return (
    <>
      <div className="flex items-center gap-2 text-xs text-default-500">
        {currentScale ? (
          <span>Scale: {currentScale.toFixed(1)} px/{currentUnit}</span>
        ) : (
          <span>No scale set</span>
        )}
      </div>

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <ModalContent>
          <ModalHeader>{mode === 'known' ? 'Known Scale / Typicals' : 'Line Scale Calibration'}</ModalHeader>
          <ModalBody>
            {mode === 'line' && pixelLength && (
              <div className="space-y-4">
                <p className="text-sm text-default-600">
                  Reference line: {Math.round(pixelLength)} pixels
                </p>
                <Input
                  label="Real-world length"
                  type="number"
                  value={realValue}
                  onValueChange={setRealValue}
                  placeholder="e.g., 10"
                />
                <Select
                  label="Unit"
                  selectedKeys={[unit]}
                  onSelectionChange={(keys) => setUnit(Array.from(keys)[0] as string)}
                >
                  {UNITS.map(u => (
                    <SelectItem key={u.key}>{u.label}</SelectItem>
                  ))}
                </Select>
              </div>
            )}
            {mode === 'known' && (
              <p className="text-sm text-default-600">
                Select a standard architectural or engineering scale:
              </p>
            )}
            <div className={mode === 'line' ? 'mt-4' : ''}>
              {mode === 'line' && (
                <p className="text-sm font-medium mb-2">Or choose a standard scale:</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                {COMMON_SCALES.map(s => (
                  <Button
                    key={s.label}
                    size="sm"
                    variant="flat"
                    onPress={() => handleManualScale(s.ratio, s.label.includes('metric') ? 'm' : 'ft')}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={() => setIsOpen(false)}>Cancel</Button>
            {mode === 'line' && (
              <Button color="primary" onPress={handleCalibrate} isDisabled={!realValue || !pixelLength}>
                Apply
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
